/**
 * Tour interactivo de 4 pasos para el organizador la primera vez que abre su
 * admin. LocalStorage `cc_tour_done_{slug}` evita repetirlo.
 *
 * Rediseño: modal editorial con eyebrow mono + título Spectral, barra de
 * progreso oliva y CTA brand.
 */
import { useEffect, useState } from 'react';

interface Step {
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    title: 'Comparte el enlace.',
    body: 'Tu cobro tiene un enlace público. Cópialo y pégalo en el WhatsApp del grupo. Cada persona entra, paga su parte y recibe su justificante.',
  },
  {
    title: 'WhatsApp listo, sin escribir.',
    body: 'El botón verde "WhatsApp" abre la app con el mensaje y el enlace ya puestos. No tienes que copiar nada.',
  },
  {
    title: 'Quién ha pagado, en vivo.',
    body: 'En la tabla "Quién ha pagado" ves quién va completando. Para los que faltan, un botón "Recordar" abre WhatsApp con el aviso preparado.',
  },
  {
    title: 'El dinero, a tu cuenta.',
    body: 'Vincula tu cuenta con Stripe (5 minutos). Cuando se cierre el cobro, te llega solo en 2–7 días. Hasta entonces el dinero queda en custodia segura.',
  },
];


export default function OnboardingTour({ slug }: { slug: string }) {
  const key = `cc_tour_done_${slug}`;
  const [step, setStep] = useState<number | null>(null);

  useEffect(() => {
    const done = localStorage.getItem(key);
    if (!done) {
      setTimeout(() => setStep(0), 800);
    }
  }, [key]);

  const skip = () => {
    localStorage.setItem(key, '1');
    setStep(null);
  };

  const next = () => {
    if (step === null) return;
    if (step >= STEPS.length - 1) {
      skip();
    } else {
      setStep(step + 1);
    }
  };

  if (step === null) return null;

  const current = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm flex items-center justify-center p-4 font-sans">
      <div className="bg-paper border border-line shadow-elevated rounded-card max-w-md w-full p-7">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
            · Paso {step + 1} de {STEPS.length}
          </span>
          <button
            onClick={skip}
            className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink"
          >
            Saltar
          </button>
        </div>

        <h2 className="font-display font-bold text-[24px] text-ink leading-tight tracking-[-0.02em] mb-2">
          {current.title}
        </h2>
        <p className="text-[14px] text-ink-700 mb-5 leading-relaxed">{current.body}</p>

        <div className="flex gap-1 mb-5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={
                i <= step
                  ? 'flex-1 h-1 rounded-pill bg-olive transition'
                  : 'flex-1 h-1 rounded-pill bg-line transition'
              }
            />
          ))}
        </div>

        <button
          onClick={next}
          className="w-full px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14.5px] shadow-btn hover:bg-brand-700 transition"
        >
          {step >= STEPS.length - 1 ? 'Empezar →' : 'Siguiente →'}
        </button>
      </div>
    </div>
  );
}
